/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react'

import { DriverNode } from '@/apollo/graphql'
import { FieldViewLink, OptionSelector } from '@/components'
import { t } from '@/i18n'
import { IOption, Maybe } from '@/types'
import { driverDisplayLabel, editDriverPath, generatePath } from '@/utils'

import { useParams } from 'react-router-dom'
import { useSelect } from './useSelect'

interface Props {
  creatable?: boolean
  isEditing?: boolean
  initial?: Maybe<Partial<DriverNode>>
  selected?: string
  filterCustomer?: boolean

  [x: string]: any
}

const DriverSelector: React.FC<Props> = ({
  creatable = false,
  isEditing,
  initial,
  selected,
  filterCustomer = true,
  ...rest
}) => {
  const { customerId } = useParams()
  const intCustomerId = filterCustomer ? parseInt(customerId || '') : undefined
  const { data, search, loading, pageInfo, onFetchMore, onSearch } = useSelect(
    isNaN(intCustomerId) ? undefined : intCustomerId,
  )

  const driverOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = (data || []).map(s => {
      if (!exists && initial?.id === s.id) {
        exists = true
      }

      return {
        label: driverDisplayLabel(s),
        value: s.id || '',
      }
    })
    if (!exists && initial && !search) {
      opts.unshift({
        label: driverDisplayLabel(initial),
        value: initial.id || '',
      })
    }
    return opts
  }, [data, initial, search])

  const selectedDriver = useMemo(() => {
    if (selected) {
      return driverOptions.find(c => c.value === selected)
    }

    return null
  }, [driverOptions, selected])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  if (!isEditing && selectedDriver) {
    return (
      <FieldViewLink
        label={t('Driver')}
        linkLabel={selectedDriver?.label}
        link={generatePath(editDriverPath, {
          id: selectedDriver?.value,
        })}
      />
    )
  }

  return (
    <OptionSelector
      {...rest}
      isAsync
      isSearchable
      name="driver"
      menuPlacement="top"
      label={t('driver')}
      onSearch={onSearch}
      creatable={creatable}
      options={driverOptions}
      emptyValue={emptyValue}
      optionsLoading={loading}
      onFetchMore={onFetchMore}
      disabled={!Boolean(isEditing)}
      fetchMoreStatus={pageInfo.fetchMoreStatus}
    />
  )
}

export default DriverSelector
